import React, { useState, useMemo } from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { Plus, Search, FolderKanban, ArrowRight, AlertCircle } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import NewProjectDialog, { statusLabel } from '@/components/projects/NewProjectDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { formatEuro, formatDate } from '@/lib/format';
import { TEST_IDS } from '@/constants/testIds/alvora';

async function fetchProjects([_, organisationId]) {
  if (!organisationId || !supabase) return [];
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .eq('organisation_id', organisationId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export default function ProjectsPage() {
  const { organisation, organisationId, role } = useAuth();
  const isManager = ['owner', 'admin', 'manager'].includes(role || '');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const { data: projects, error, isLoading, mutate } = useSWR(
    organisationId ? ['projects-list', organisationId] : null,
    fetchProjects
  );

  const archivedCount = useMemo(() => (projects || []).filter((p) => p.status === 'archived').length, [projects]);
  const filtered = useMemo(() => {
    if (!projects) return [];
    const q = search.trim().toLowerCase();
    const visible = showArchived ? projects : projects.filter((p) => p.status !== 'archived');
    if (!q) return visible;
    return visible.filter((p) =>
      [p.name, p.project_code, p.client_name, p.site_address, p.project_type, p.status]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [projects, search, showArchived]);

  return (
    <div data-testid={TEST_IDS.projectsPage} className="p-6 md:p-8 max-w-7xl mx-auto">
      <PageHeader
        title="Projects"
        subtitle={organisation ? `${organisation.name} · All projects` : 'All projects'}
        actions={
          <Button
            data-testid={TEST_IDS.newProjectButton}
            onClick={() => setDialogOpen(true)}
            className="bg-bronze hover:bg-bronze-hover text-white transition-colors duration-150"
          >
            <Plus size={16} className="mr-1.5" /> New project
          </Button>
        }
      />

      {/* Toolbar */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative sm:w-80">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid={TEST_IDS.projectsSearch}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, code, client…"
            className="pl-9 h-10"
          />
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          {archivedCount > 0 && (
            <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="projects-show-archived">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[#957C5D]" />
              Show archived ({archivedCount})
            </label>
          )}
          <span data-testid="projects-count">{projects ? `${filtered.length} of ${projects.length - (showArchived ? 0 : archivedCount)} projects` : ''}</span>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="rounded-lg border border-border bg-white p-6 text-sm text-muted-foreground">
          Loading projects…
        </div>
      ) : error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5" />
          <div>
            <div className="font-medium">Could not load projects</div>
            <div className="mt-0.5">{error.message}</div>
            <div className="mt-1 text-xs">
              If this is a permissions error, add an RLS SELECT policy on <code>projects</code>
              {' '}for members of the project's organisation.
            </div>
          </div>
        </div>
      ) : !filtered || filtered.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title={projects && projects.length === 0 ? 'No projects yet' : 'No projects match your search'}
          description={
            projects && projects.length === 0
              ? 'Create your first project to start tracking budgets, costs and progress.'
              : 'Try a different search term or clear the search box.'
          }
          action={
            projects && projects.length === 0 ? (
              <Button
                data-testid={TEST_IDS.createFirstProject}
                onClick={() => setDialogOpen(true)}
                className="bg-bronze hover:bg-bronze-hover text-white transition-colors duration-150"
              >
                <Plus size={16} className="mr-1.5" /> Create first project
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="rounded-lg border border-border bg-white overflow-hidden">
          <Table data-testid={TEST_IDS.projectsTable}>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground">Code</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground">Project</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground">Client</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground">Type</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground">Status</TableHead>
                <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground">Start</TableHead>
                {isManager && <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground text-right">Contract</TableHead>}
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => (
                <TableRow
                  key={p.id}
                  data-testid={TEST_IDS.projectRow}
                  className="group"
                >
                  <TableCell className="font-mono text-xs text-charcoal">
                    {p.project_code || '—'}
                  </TableCell>
                  <TableCell className="font-medium text-sm text-charcoal">
                    <Link
                      to={`/projects/${p.id}`}
                      className="hover:text-bronze transition-colors duration-150"
                    >
                      {p.name || 'Untitled project'}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm text-charcoal">{p.client_name || '—'}</TableCell>
                  <TableCell className="text-sm text-charcoal">{p.project_type || '—'}</TableCell>
                  <TableCell>
                    {p.status ? (
                      <Badge variant="outline" className="text-[11px] uppercase tracking-wider">
                        {statusLabel(p.status)}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-charcoal">
                    {p.start_date ? formatDate(p.start_date) : '—'}
                  </TableCell>
                  {isManager && <TableCell className="text-sm text-charcoal text-right tabular-nums">
                    {p.contract_value_ex_vat != null ? formatEuro(p.contract_value_ex_vat) : '—'}
                  </TableCell>}
                  <TableCell>
                    <Link
                      to={`/projects/${p.id}`}
                      className="text-muted-foreground group-hover:text-bronze transition-colors duration-150 inline-flex items-center"
                      aria-label="Open project"
                    >
                      <ArrowRight size={16} />
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <NewProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={() => mutate()}
      />
    </div>
  );
}
